import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';

// Increment-based pulse store. The panel core calls `triggerFlash()` after
// a page-action result lands; subscribed pages observe the new tick and
// flip a local boolean for `FLASH_DURATION_MS` so they can style the
// resulting `is-just-applied` state via their own SCSS module.
//
// We use an integer counter (not a boolean) so back-to-back actions —
// e.g. "find hotels in Portugal" then "now in Spain" — each fire their
// own flash even if the previous one hasn't finished.
//
// Generic by design: nothing here knows about products. New consumer
// pages call `useIsJustApplied()` and pass the boolean to `pickCss` (or
// a className combinator) so the active state lives inside their CSS
// Module scope.

type FlashStore = {
  tick: number;
  triggerFlash: () => void;
};

export const usePageActionFlashStore = create<FlashStore>((set) => ({
  tick: 0,
  triggerFlash: () => set((s) => ({ tick: s.tick + 1 })),
}));

export const FLASH_DURATION_MS = 1_000;

/**
 * Returns true for {@link FLASH_DURATION_MS} after the panel core fires a
 * page-action flash, then flips back to false. Pages compose this with
 * their SCSS module via `pickCss({ isJustApplied })` to style the
 * transient highlight inside their own scope.
 */
export const useIsJustApplied = (): boolean => {
  const tick = usePageActionFlashStore((s) => s.tick);
  // Skip the initial mount tick=0; only react to later increments.
  const previousTickRef = useRef(tick);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (tick === previousTickRef.current) return;
    previousTickRef.current = tick;
    setActive(true);
    const timeout = window.setTimeout(() => {
      setActive(false);
    }, FLASH_DURATION_MS);
    return () => {
      window.clearTimeout(timeout);
      setActive(false);
    };
  }, [tick]);

  return active;
};
